import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { BookingQueueStatusService, OutboxRow } from 'app/services/booking-queue-status.service';
import { ToasterService } from 'app/services/toaster.service';

/**
 * Toolbar chip for the booking durable request queue (Workstream A outbox):
 * shows pending / processing / failed counts so the operator sees queued or
 * stuck bookings without opening a diagnostic screen. Clicking opens a panel
 * with the counts and, when present, the FAILED rows with requeue / discard
 * actions (the reconciliation endpoints shipped with Workstream A).
 */
@Component({
    selector: 'app-booking-queue-indicator',
    templateUrl: './booking-queue-indicator.component.html',
    styleUrls: ['./booking-queue-indicator.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookingQueueIndicatorComponent implements OnInit, OnDestroy {

    private queue = inject(BookingQueueStatusService);
    private toastr = inject(ToasterService);

    readonly open = signal(false);
    readonly failedList = signal<OutboxRow[]>([]);
    readonly loadingFailed = signal(false);
    /** Two-step discard: first click arms this id, second click confirms. */
    readonly confirmDiscardId = signal<number | null>(null);
    readonly busyId = signal<number | null>(null);

    readonly pending = computed(() => this.queue.summary()?.PENDING ?? 0);
    readonly processing = computed(() => this.queue.summary()?.PROCESSING ?? 0);
    readonly failed = computed(() => this.queue.summary()?.FAILED ?? 0);
    readonly unreachable = this.queue.unreachable;
    readonly active = computed(() => this.pending() + this.processing());

    private disarmTimer: any = null;

    ngOnInit() {
        this.queue.startPolling();
    }

    ngOnDestroy() {
        this.queue.stopPolling();
        if (this.disarmTimer) {
            clearTimeout(this.disarmTimer);
        }
    }

    toggle() {
        this.open.update(v => !v);
        if (this.open()) {
            this.queue.refresh();
            this.loadFailed();
        }
    }

    close() {
        this.open.set(false);
        this.confirmDiscardId.set(null);
    }

    loadFailed() {
        this.loadingFailed.set(true);
        this.queue.failedRows().subscribe({
            next: (resp) => {
                this.failedList.set(resp?.result || []);
                this.loadingFailed.set(false);
            },
            error: () => {
                this.failedList.set([]);
                this.loadingFailed.set(false);
            }
        });
    }

    requeue(row: OutboxRow) {
        this.busyId.set(row.id);
        this.queue.requeue(row.id).subscribe({
            next: () => {
                this.toastr.success('Booking re-queued for processing', 'Queue');
                this.afterAction();
            },
            error: (err) => {
                this.toastr.error(err?.error?.message || 'Re-queue failed', 'Queue');
                this.busyId.set(null);
            }
        });
    }

    discard(row: OutboxRow) {
        if (this.confirmDiscardId() !== row.id) {
            this.confirmDiscardId.set(row.id);
            if (this.disarmTimer) {
                clearTimeout(this.disarmTimer);
            }
            this.disarmTimer = setTimeout(() => this.confirmDiscardId.set(null), 4000);
            return;
        }
        this.confirmDiscardId.set(null);
        this.busyId.set(row.id);
        this.queue.discard(row.id).subscribe({
            next: (resp) => {
                this.toastr.success('Booking discarded; reserved capacity released', 'Queue');
                (resp?.result || []).forEach((note: string) => this.toastr.warning(note, 'Queue'));
                this.afterAction();
            },
            error: (err) => {
                this.toastr.error(err?.error?.message || 'Discard failed', 'Queue');
                this.busyId.set(null);
            }
        });
    }

    private afterAction() {
        this.busyId.set(null);
        this.queue.refresh();
        this.loadFailed();
    }
}
